import { useState, useEffect, useRef } from 'preact/hooks'

export interface UseSelectVisibilityOptions {
  enableSearch?: boolean
}

export const useSelectVisibility = ({ enableSearch }: UseSelectVisibilityOptions = {}) => {
  const [isSelectVisible, setSelectVisibility] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const triggerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const lastFocusedOptionIndexRef = useRef(0)

  const getOptions = (): HTMLElement[] =>
    Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [])

  const rememberFocusedOption = (options = getOptions()): void => {
    const activeIndex = options.indexOf(document.activeElement as HTMLElement)

    if (activeIndex >= 0) {
      lastFocusedOptionIndexRef.current = activeIndex
    }
  }

  const toggleSelectVisibility = (): void => {
    setSelectVisibility((prev) => !prev)
  }

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as HTMLElement)) {
        rememberFocusedOption()
        setSelectVisibility(false)
      }
    }
    document.body.addEventListener('click', handleClick)
    return () => document.body.removeEventListener('click', handleClick)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && containerRef.current?.contains(document.activeElement as HTMLElement)) {
        rememberFocusedOption()
        setSelectVisibility(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (isSelectVisible) {
      if (enableSearch) {
        searchInputRef.current?.focus()
        return
      }

      const options = getOptions()
      const optionToFocus = options[lastFocusedOptionIndexRef.current] ?? options[0]

      optionToFocus?.focus()
    } else if (enableSearch) {
      setSearchValue('')
    }
  }, [isSelectVisible])

  const handleTriggerKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleSelectVisibility()
    } else if (e.key === 'ArrowDown' && !isSelectVisible) {
      e.preventDefault()
      setSelectVisibility(true)
    }
  }

  const handleListKeyDown = (e: KeyboardEvent): void => {
    const options = getOptions()
    const isSearchInputTarget = enableSearch && e.target === searchInputRef.current

    if (isSearchInputTarget) {
      if (e.key === 'Enter') {
        e.preventDefault()
        const [firstOption] = options

        if (firstOption) {
          lastFocusedOptionIndexRef.current = 0
          firstOption.focus()
        }
        return
      }

      // Space inside the search input must keep its default typing behavior.
      if (e.key === ' ') {
        return
      }
    }

    const currentIndex = options.indexOf(document.activeElement as HTMLElement)
    const currentOption = options[currentIndex]

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const nextIndex = currentIndex + 1 < options.length ? currentIndex + 1 : 0
      const next = options[nextIndex]

      if (next) {
        lastFocusedOptionIndexRef.current = nextIndex
      }

      next?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1
      const prev = options[prevIndex]

      if (prev) {
        lastFocusedOptionIndexRef.current = prevIndex
      }

      prev?.focus()
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      rememberFocusedOption(options)
      currentOption?.click()
      setSelectVisibility(false)
      triggerRef.current?.focus()
    }
  }

  return {
    isSelectVisible,
    toggleSelectVisibility,
    triggerRef,
    containerRef,
    listRef,
    handleTriggerKeyDown,
    handleListKeyDown,
    searchValue,
    setSearchValue,
    searchInputRef,
  }
}
